/**
 * Outlook Service - Microsoft Graph API Integration
 * Handles Outlook email operations via Microsoft 365
 */
import api from './api';

const outlookService = {
  /**
   * Prüft den Status der Outlook-Integration
   */
  getStatus: async () => {
    const response = await api.get('/outlook/status');
    return response.data;
  },

  /**
   * Initialisiert den OAuth2-Flow für Microsoft
   * @returns {Object} { authorization_url, state }
   */
  initAuth: async () => {
    const response = await api.get('/outlook/auth/init');
    return response.data;
  },

  /**
   * Verarbeitet den OAuth2-Callback
   * @param {string} code - Authorization Code von Microsoft
   * @param {string} state - State-Parameter für CSRF-Schutz
   */
  handleCallback: async (code, state) => {
    const response = await api.post('/outlook/auth/callback', { code, state });
    return response.data;
  },

  /**
   * Gibt alle verbundenen Outlook-Konten zurück
   */
  getAccounts: async () => {
    const response = await api.get('/outlook/accounts');
    return response.data;
  },

  /**
   * Trennt ein Outlook-Konto
   * @param {string} accountId - ID des Outlook-Kontos
   */
  disconnectAccount: async (accountId) => {
    const response = await api.delete(`/outlook/accounts/${accountId}`);
    return response.data;
  },

  /**
   * Setzt ein Outlook-Konto als Standard
   * @param {string} accountId - ID des Outlook-Kontos
   */
  setDefaultAccount: async (accountId) => {
    const response = await api.post(`/outlook/accounts/${accountId}/set-default`);
    return response.data;
  },

  /**
   * Ruft E-Mails aus dem Outlook-Posteingang ab
   * @param {Object} params - Optionale Parameter
   * @param {string} params.accountId - Spezifisches Outlook-Konto
   * @param {string} params.folder - E-Mail-Ordner (inbox, sentitems, etc.)
   * @param {number} params.top - Anzahl der E-Mails
   * @param {number} params.skip - Offset für Pagination
   * @param {boolean} params.unreadOnly - Nur ungelesene E-Mails
   */
  getInbox: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.accountId) queryParams.append('account_id', params.accountId);
    if (params.folder) queryParams.append('folder', params.folder);
    if (params.top) queryParams.append('top', params.top);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.unreadOnly) queryParams.append('unread_only', 'true');
    
    const response = await api.get(`/outlook/inbox?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Ruft eine einzelne E-Mail mit vollständigem Inhalt ab
   * @param {string} messageId - ID der E-Mail
   * @param {string} accountId - Optional: Spezifisches Outlook-Konto
   */
  getMessage: async (messageId, accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.get(`/outlook/inbox/${messageId}${queryParams}`);
    return response.data;
  },

  /**
   * Sendet eine E-Mail über Outlook
   * @param {Object} emailData - E-Mail-Daten
   * @param {Array<string>} emailData.to - Empfänger
   * @param {string} emailData.subject - Betreff
   * @param {string} emailData.body - Inhalt
   * @param {boolean} emailData.is_html - HTML-Format
   * @param {Array<string>} emailData.cc - CC-Empfänger
   * @param {Array<string>} emailData.bcc - BCC-Empfänger
   * @param {string} accountId - Optional: Spezifisches Outlook-Konto
   */
  sendEmail: async (emailData, accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.post(`/outlook/send${queryParams}`, emailData);
    return response.data;
  },

  /**
   * Markiert eine E-Mail als gelesen
   * @param {string} messageId - ID der E-Mail
   * @param {string} accountId - Optional: Spezifisches Outlook-Konto
   */
  markAsRead: async (messageId, accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.post(`/outlook/inbox/${messageId}/mark-read${queryParams}`);
    return response.data;
  },

  /**
   * Markiert eine E-Mail als ungelesen
   * @param {string} messageId - ID der E-Mail
   * @param {string} accountId - Optional: Spezifisches Outlook-Konto
   */
  markAsUnread: async (messageId, accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.post(`/outlook/inbox/${messageId}/mark-unread${queryParams}`);
    return response.data;
  },

  /**
   * Ruft alle E-Mail-Ordner ab
   * @param {string} accountId - Optional: Spezifisches Outlook-Konto
   */
  getFolders: async (accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.get(`/outlook/folders${queryParams}`);
    return response.data;
  },

  /**
   * Gibt die Anzahl ungelesener E-Mails zurück
   * @param {string} accountId - Optional: Spezifisches Outlook-Konto
   * @param {string} folder - E-Mail-Ordner
   */
  getUnreadCount: async (accountId = null, folder = 'inbox') => {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);
    queryParams.append('folder', folder);
    
    const response = await api.get(`/outlook/unread-count?${queryParams.toString()}`);
    return response.data;
  },

  // ============================================================
  // KALENDER-FUNKTIONEN
  // ============================================================

  /**
   * Ruft Kalender-Termine aus Outlook ab
   * @param {Object} params - Parameter
   * @param {string} params.accountId - Outlook-Konto-ID
   * @param {string} params.start - Start-Datum (ISO 8601)
   * @param {string} params.end - End-Datum (ISO 8601)
   * @param {number} params.top - Maximale Anzahl
   */
  getCalendarEvents: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.accountId) queryParams.append('account_id', params.accountId);
    if (params.start) queryParams.append('start', params.start);
    if (params.end) queryParams.append('end', params.end);
    if (params.top) queryParams.append('top', params.top);
    
    const response = await api.get(`/outlook/calendar/events?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Erstellt einen neuen Outlook-Termin
   * @param {Object} eventData - Termin-Daten
   * @param {string} eventData.subject - Betreff
   * @param {string} eventData.start_datetime - Startzeit
   * @param {string} eventData.end_datetime - Endzeit
   * @param {string} eventData.location - Ort
   * @param {string} eventData.body - Beschreibung
   * @param {Array<string>} eventData.attendees - Teilnehmer
   * @param {boolean} eventData.is_all_day - Ganztägig
   * @param {string} accountId - Outlook-Konto-ID
   */
  createCalendarEvent: async (eventData, accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.post(`/outlook/calendar/events${queryParams}`, eventData);
    return response.data;
  },

  /**
   * Aktualisiert einen Outlook-Termin
   * @param {string} eventId - Termin-ID
   * @param {Object} updateData - Zu aktualisierende Felder
   * @param {string} accountId - Outlook-Konto-ID
   */
  updateCalendarEvent: async (eventId, updateData, accountId = null) => {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);
    if (updateData.subject) queryParams.append('subject', updateData.subject);
    if (updateData.start) queryParams.append('start', updateData.start);
    if (updateData.end) queryParams.append('end', updateData.end);
    if (updateData.location) queryParams.append('location', updateData.location);
    
    const response = await api.patch(`/outlook/calendar/events/${eventId}?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Löscht einen Outlook-Termin
   * @param {string} eventId - Termin-ID
   * @param {string} accountId - Outlook-Konto-ID
   */
  deleteCalendarEvent: async (eventId, accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.delete(`/outlook/calendar/events/${eventId}${queryParams}`);
    return response.data;
  },

  /**
   * Ruft alle verfügbaren Outlook-Kalender ab
   * @param {string} accountId - Outlook-Konto-ID
   */
  getCalendars: async (accountId = null) => {
    const queryParams = accountId ? `?account_id=${accountId}` : '';
    const response = await api.get(`/outlook/calendar/calendars${queryParams}`);
    return response.data;
  },

  // ==========================================
  // KONTAKTE-FUNKTIONEN
  // ==========================================

  /**
   * Ruft Outlook-Kontakte ab
   * @param {string} accountId - Outlook-Konto-ID
   * @param {Object} options - Optionen (skip, limit, search)
   */
  getContacts: async (accountId, options = {}) => {
    const { skip = 0, limit = 50, search = '' } = options;
    const params = new URLSearchParams({
      account_id: accountId,
      skip: skip.toString(),
      limit: limit.toString()
    });
    if (search) params.append('search', search);
    
    const response = await api.get(`/outlook/contacts?${params.toString()}`);
    return response.data;
  },

  /**
   * Ruft einen einzelnen Outlook-Kontakt ab
   * @param {string} contactId - Kontakt-ID
   * @param {string} accountId - Outlook-Konto-ID
   */
  getContact: async (contactId, accountId) => {
    const response = await api.get(`/outlook/contacts/${contactId}?account_id=${accountId}`);
    return response.data;
  },

  /**
   * Erstellt einen neuen Outlook-Kontakt
   * @param {Object} contactData - Kontakt-Daten
   * @param {string} accountId - Outlook-Konto-ID
   */
  createContact: async (contactData, accountId) => {
    const response = await api.post(`/outlook/contacts?account_id=${accountId}`, contactData);
    return response.data;
  },

  /**
   * Aktualisiert einen Outlook-Kontakt
   * @param {string} contactId - Kontakt-ID
   * @param {Object} contactData - Zu aktualisierende Felder
   * @param {string} accountId - Outlook-Konto-ID
   */
  updateContact: async (contactId, contactData, accountId) => {
    const response = await api.patch(`/outlook/contacts/${contactId}?account_id=${accountId}`, contactData);
    return response.data;
  },

  /**
   * Löscht einen Outlook-Kontakt
   * @param {string} contactId - Kontakt-ID
   * @param {string} accountId - Outlook-Konto-ID
   */
  deleteContact: async (contactId, accountId) => {
    const response = await api.delete(`/outlook/contacts/${contactId}?account_id=${accountId}`);
    return response.data;
  },

  /**
   * Startet die Kontakt-Synchronisation
   * @param {string} accountId - Outlook-Konto-ID
   * @param {string} direction - Richtung: "from_outlook", "to_outlook", "both"
   */
  syncContacts: async (accountId, direction = 'both') => {
    const response = await api.post('/outlook/contacts/sync', {
      account_id: accountId,
      direction: direction
    });
    return response.data;
  },

  /**
   * Ruft den Synchronisations-Status ab
   * @param {string} accountId - Outlook-Konto-ID
   */
  getSyncStatus: async (accountId) => {
    const response = await api.get(`/outlook/contacts/sync/status?account_id=${accountId}`);
    return response.data;
  },

  // ==========================================
  // AUTO-SYNC FUNKTIONEN
  // ==========================================

  /**
   * Aktiviert die automatische Synchronisation (alle 5 Minuten)
   * @param {string} accountId - Outlook-Konto-ID
   */
  enableAutoSync: async (accountId) => {
    const response = await api.post(`/outlook/contacts/auto-sync/enable?account_id=${accountId}`);
    return response.data;
  },

  /**
   * Deaktiviert die automatische Synchronisation
   * @param {string} accountId - Outlook-Konto-ID
   */
  disableAutoSync: async (accountId) => {
    const response = await api.post(`/outlook/contacts/auto-sync/disable?account_id=${accountId}`);
    return response.data;
  },

  /**
   * Ruft den Auto-Sync-Status ab
   * @param {string} accountId - Outlook-Konto-ID
   */
  getAutoSyncStatus: async (accountId) => {
    const response = await api.get(`/outlook/contacts/auto-sync/status?account_id=${accountId}`);
    return response.data;
  }
};

export default outlookService;
